import * as React from "react";
import { Dropdown } from "@bizform/primereact/components/dropdown/Dropdown";
import { DropdownProps } from "@bizform/primereact/dropdown";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import { Static } from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic,
    ComponentEventType
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { LookUp } from "@bizform/type/model/data_type/LookUp.model";
import { DataType } from "@bizform/type/model/model/DataType.model";
import {
    AccessibilityDropdown,
    ComponentHelper,
    Debug,
    DomHelper,
    ObjectHelper
} from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../AbstractComponentElement";
import { I18n } from "../../misc/model/I18n.model";


let ___dropdown;

type Props = DropdownProps & {
};

type WrappedCompState = {
    // attribute
    value?: any;
    tabindex?: number;
    options?: any[];
    optionLabel?: string;
    style?: object;
    className?: string;
    printStyle?: object;
    printClassName?: string;
    autoWidth?: boolean;
    scrollHeight?: string;
    filter?: boolean;
    filterBy?: string;
    filterPlaceholder?: string;
    editable?: boolean;
    placeholder?: string;
    // required?: boolean;
    disabled?: boolean;
    appendTo?: any;
    tabIndex?: number;
    autoFocus?: boolean;
    lazy?: boolean;
    panelClassName?: string;
    panelStyle?: object;
    dataKey?: string;
    inputId?: string;
    showClear?: boolean;
    tooltipAttribute?: TooltipOptions & {text?: string};

    // event
    onChange?(e: ComponentEventType): void;
    itemTemplate?(option: any): React.ReactNode;
    onMouseDown?(event: Event): void;
    onContextMenu?(event: Event): void;
};

type WrappedFormState = {
    onChange?: string;
    onMouseDown?: string;
    onContextMenu?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * Dropdown component element
 *
 * @since 0.0.1
 * @author Byungmin Choi, Hwansoon Park
 * @class
 */
@ComponentElementClass()
@Static<ComponentElementStatic<LookUp>>()
export class BfDropdown extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "__iKIdAkyWB0PZhSnT_M_hZJcUgGzC8V",
        category: ComponentCategory.INPUT,
        label: I18n.componentElement.dropdown.label,
        description: I18n.componentElement.dropdown.description,
        icon: "",
        dataType: [DataType.LOOKUP],
        property: null
    };

    focus(): void {
        if (this.accessibility) {
            this._dropdown.validationFocus();
        } else if (this._dropdown && this._dropdown.container) {
           this._dropdown.focusInput.focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, ComponentEventType>(property, this);
            newState.wrappedCompState.editable = newState.wrappedCompState.editable ? true : false;
            newState.wrappedCompState.showClear = newState.wrappedCompState.showClear ? true : false;
            newState.wrappedCompState.filter = newState.wrappedCompState.filter ? true : false;
            newState.wrappedCompState.autoWidth = newState.wrappedCompState.autoWidth ? true : false;
            if (this._init === null) {
                this._init = true;
            }
            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: LookUp): void {
    }

    emitFormState(value: string): void {
    }

    subscription(subscriptionValue: LookUp): void {
        const newState = {
            wrappedCompState: {
                ...this.state.wrappedCompState
            }
        };

        if (!ComponentHelper.isNull(subscriptionValue)) {
            if (!ComponentHelper.isNull(subscriptionValue.value)) {
                const realValue = newState.wrappedCompState.options.find((option) => {
                    return option.value === subscriptionValue.value;
                });
                if (realValue) {
                    newState.wrappedCompState.value = realValue.value;
                    this.setState(newState);
                }
            } else {
                newState.wrappedCompState.value = null;
                this.setState(newState);
            }
        } else {
            newState.wrappedCompState.value = null;
            this.setState(newState);
        }
    }

    lookupDataSubscription?(lookup: Array<LookUp>): void {
        if (Array.isArray(lookup)) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    options: lookup
                }
            };
            this.setState(newState);
        }
    }

    @ReactComponentElementInit(BfDropdown)
    static init(): ComponentElement<LookUp> {
        return;
    }

    subscriptionResize(): void {
        if (___dropdown) {
            for (const panelName in ___dropdown) {
                const panel = ___dropdown[panelName];
                if (panel._timer) {
                    clearTimeout(panel._timer);
                }
                panel._timer = setTimeout(panel.alignPanel, 100);
            }
        }
    }

    initDone(): void {
        ___dropdown[this.elementId] = this;
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _dropdown: any;
    private _preRef: any;
    private _init: boolean;
    private _readonly: boolean;
    private _editable: boolean;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                placeholder: null,
                options: [],
                optionLabel: null,
                editable: null,
                showClear: null,
                value: ""
            },
            wrappedFormState: {}
        };

        this._onChangeEvent = this._onChangeEvent.bind(this);
        this._onMouseDownEvent = this._onMouseDownEvent.bind(this);
        this._onContextMenuEvent = this._onContextMenuEvent.bind(this);
        this._containerKeyupEventListener = this._containerKeyupEventListener.bind(this);
        this._filterKeyupEventListener = this._filterKeyupEventListener.bind(this);
        this._setRef = this._setRef.bind(this);

        this.subscriptionResize = this.subscriptionResize.bind(this);
        this.closeOverlay = this.closeOverlay.bind(this);
        this.alignPanel = this.alignPanel.bind(this);
        if (!___dropdown) {
            ___dropdown = {};
        }
    }

    private _onChangeEvent(e: ComponentEventType): void {
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: e.value
                }
            };
            this.setState(newState, this._onChangeEventCallback);
        }
    }

    private _onMouseDownEvent(e: ComponentEventType): void {
    }

    private _onContextMenuEvent(e: ComponentEventType): void {
    }

    private _callbackSetState() {
        this.emit(this.state.wrappedCompState.options.find((lookUp) => {
            return String(lookUp.value) === String(this.state.wrappedCompState.value);
        }));
    }

    private _onChangeEventCallback() {
        this._callbackSetState();
    }

    componentDidUpdate(prevProps, prevState): void {
        if (!this.accessibility && this._dropdown) {
            if (this._preRef && this._dropdown !== this._preRef) {
                this._removeCustom(true);
            }
            if (this.state.displayMode === ComponentElementDisplayMode.READONLY) {
                if (this._readonly !== true) {
                    this._readonly = true;
                    this._editable = false;
                    if (this._dropdown.focusInput) {
                        if (!this._dropdown.focusInput.hasAttribute("readonly")) {
                            this._dropdown.focusInput.setAttribute("readonly", true);
                        }
                    }
                    if (this._dropdown.container) {
                        this._dropdown.container.removeEventListener("keyup", this._containerKeyupEventListener);
                        this._dropdown.container.addEventListener("click", ComponentHelper.stopEvent);
                    }
                    if (this._dropdown.filterInput) {
                        this._dropdown.filterInput.removeEventListener("keyup", this._filterKeyupEventListener);
                    }
                }
            } else if (!this.accessibility && this.state.displayMode === ComponentElementDisplayMode.EDIT) {
                if (this._editable !== true) {
                    this._readonly = false;
                    this._editable = true;
                    if (this._dropdown) {
                        if (this._dropdown.focusInput) {
                            if (this._dropdown.focusInput.hasAttribute("readonly")) {
                                this._dropdown.focusInput.removeAttribute("readonly");
                            }
                        }
                        if (this._dropdown.container) {
                            this._dropdown.container.addEventListener("keyup", this._containerKeyupEventListener);
                            this._dropdown.container.removeEventListener("click", ComponentHelper.stopEvent);
                        }
                        if (this._dropdown.filterInput) {
                            this._dropdown.filterInput.addEventListener("keyup", this._filterKeyupEventListener);
                        }
                    }
                }
            }
        }
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        if (!this._init) {
            this._preRef = this._dropdown;
        } else {
            this._removeCustom(true);
            this._preRef = null;
        }
        this._init = null;
        return ObjectHelper.hasDifference(nextState, this.state);
    }

    componentWillUnmount(): void {
        this._removeCustom();
        this._dropdown = null;
        delete ___dropdown[this.elementId];
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    emitSubscriptionReject(): void {
    }

    closeOverlay(): void {
        if (this._dropdown && this._dropdown.hide) {
            this._dropdown.hide();
        }
    }

    alignPanel() {
        if (this._dropdown && this._dropdown.alignPanel) {
            this._dropdown.alignPanel();
        }
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            if (this.accessibility && this.displayMode !== ComponentElementDisplayMode.PRINT) {
                return this._render4Edit();
            }
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                    return this._render4ReadOnly();
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: WrappedCompState): React.ReactNode {
        delete(newProps["printClassName"]);
        delete(newProps["printStyle"]);
        if (this.accessibility) {
            return (
                <AccessibilityDropdown
                    tabindex={newProps.tabindex}
                    key={this.elementId}
                    id={this.elementId}
                    value={newProps.value}
                    options={newProps.options}
                    placeholder={newProps.placeholder}
                    showClear={newProps.showClear}
                    isMultiple={false}
                    displayMode={this.displayMode}
                    tooltipAttribute={newProps.tooltipAttribute}
                    onChange={newProps.onChange}
                    onMouseDown={newProps.onMouseDown}
                    onContextMenu={newProps.onContextMenu}
                    ref={this._setRef}
                />
            );
        } else {
            if (newProps.disabled) {
                newProps.tabIndex = -1;
            }
            const key = ComponentHelper.tooltipProps(newProps, this.elementId, this.accessibility);
            newProps.tabIndex = newProps.tabindex;
            return (
                <Dropdown
                    {...newProps}
                    key={key}
                    id={this.elementId}
                    autoWidth={false}
                    editable={false}
                    appendTo={DomHelper.getForOverLayDom()}
                    ref={this._setRef}
                />
            );
        }
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState
        };

        return this._render(newProps);
    }

    private _render4ReadOnly(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            readOnly: true,
            showClear: false
        };

        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            disabled: true,
            showClear: false
        };

        return this._render(newProps);
    }

    private _render4Print(): React.ReactNode {
        let printValue = null;
        if (this.state.wrappedCompState.options && this.state.wrappedCompState.options.length > 0) {
            printValue = this.state.wrappedCompState.options.find((lookUp) => {
                return lookUp.value === this.state.wrappedCompState.value;
            });
        }
        return (
            <div ref={this._setRef} style={this.state.wrappedCompState ? {...this.state.wrappedCompState.printStyle} : null} className={`bf-print-mode ${this.state.wrappedCompState ? this.state.wrappedCompState.printClassName : null}`}>{printValue ? printValue.label : ""}</div>
        );
    }

    private _setRef(ref) {
        if (ref) {
            this._dropdown = ref;
            this.readyComponent();
        }
    }

    private _containerKeyupEventListener(e) {
        if (this._dropdown && this._dropdown.container) {
            if (e.keyCode === 13) {
                this._dropdown.show();
                this._dropdown.filterInput ? this._dropdown.filterInput.focus() : null;
            }
        }
    }

    private _filterKeyupEventListener(e) {
        if (this._dropdown && this._dropdown.filterInput) {
            if (e.keyCode === 13) {
                this._dropdown.focusInput.focus();
            }
        }
    }

    private _removeCustom(preRefMode?: boolean): void {
        if (this.accessibility) {
            return;
        }
        const ref = preRefMode ? this._preRef : this._preRef || this._dropdown;
        if (ref) {
            if (ref.focusInput) {
                if (ref.focusInput.hasAttribute("readonly")) {
                    ref.focusInput.removeAttribute("readonly");
                }
            }
            if (ref.container) {
                ref.container.removeEventListener("click", ComponentHelper.stopEvent);
                ref.container.removeEventListener("keyup", this._containerKeyupEventListener);
            }
            if (ref.filterInput) {
                ref.filterInput.removeEventListener("keyup", this._filterKeyupEventListener);
            }
        }
    }
}
